import { DateTime } from '@ksyos/date-time';
import { Card, FormGroup } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { ValidationErrors } from '../../../../utils/validation';
import type { IKsyosAccountDetails, IKsyosProfileDetails } from './addKsyosEmployeeState';
import { ksyosAccountDetailsSchema } from '../../../../../common/validation/practitioner';
import { translateKey } from '../../../../translate';
import { useValidationErrors } from '../../../../utils/hooks/useValidationErrors';
import { updateValidationErrorsForOne, validate } from '../../../../utils/validation';
import { DateInput } from '../../../elements/common';
import { FormContextProvider } from '../../../elements/formContext';
import { InputTextField } from '../../../userDataManagement/InputText';

interface IAccountProps {
    accountDetails?: IKsyosAccountDetails;
    onUpdateAccountDetails: (accountDetails: IKsyosAccountDetails) => void;
    onUpdateIsValid: (isValid: boolean) => void;
    profileDetails?: IKsyosProfileDetails;
    serverErrors: ValidationErrors;
}

export function Account(props: IAccountProps) {
    const [accountDetails, setAccountDetails] = useState<IKsyosAccountDetails>(
        props.accountDetails ?? getInitialAccountDetails()
    );
    const [errors, setErrors] = useValidationErrors();

    useEffect(() => {
        const { hasErrors } = validate(ksyosAccountDetailsSchema, accountDetails);

        props.onUpdateAccountDetails(accountDetails);
        props.onUpdateIsValid(!hasErrors);
    }, [accountDetails]);

    useEffect(() => {
        setErrors((currentErrors) => currentErrors.merge(props.serverErrors));
    }, [props.serverErrors]);

    useEffect(() => {
        // The DateInput triggers the onDoValidate before the state is changed,
        // so we'll use an effect to validate the startDate...
        validateOne('startDate');
    }, [accountDetails.startDate]);

    return (
        <FormContextProvider
            value={{
                placeholderGroupName: 'accountDetails',
                item: accountDetails,
                errors,
                onChange: async (key, value) => handleChange(key, value),
                onDoValidate: async (key) => validateOne(key),
            }}
        >
            <Card>
                <Card.Header title={translateKey('profile.ksyosAccount')} />
                <Card.Body>
                    <FormGroup
                        isRequired
                        label={translateKey('common.startDate')}
                    >
                        <DateInput
                            error={errors.get('startDate')}
                            fieldKey="startDate"
                            hasMaxWidth={true}
                            onDoValidate={() => {
                                /* Validated by effect */
                            }}
                            placeholder={translateKey('common.dateFormat')}
                            variant="outline"
                        />
                    </FormGroup>

                    {renderTextInput('common.userName', 'userName', true)}
                </Card.Body>
            </Card>
        </FormContextProvider>
    );

    function renderTextInput(labelKey: string, fieldKey: keyof IKsyosAccountDetails, isRequired: boolean = false) {
        return (
            <InputTextField
                error={errors.get(fieldKey)}
                fieldKey={fieldKey}
                hasErrors={errors.has(fieldKey)}
                isRequired={isRequired}
                label={labelKey}
                nullable={!isRequired}
                placeholder={`${translateKey('common.enterValuePlaceholder', translateKey(labelKey))}`}
            />
        );
    }

    function handleChange<K extends keyof IKsyosAccountDetails>(name: K, value: IKsyosAccountDetails[K]) {
        setAccountDetails((currentAccountDetails) => ({ ...currentAccountDetails, [name]: value }));
    }

    function validateOne<K extends keyof IKsyosAccountDetails>(name: K) {
        setErrors((currentErrors) =>
            updateValidationErrorsForOne(ksyosAccountDetailsSchema, accountDetails, currentErrors, name)
        );
    }
}

export function getInitialAccountDetails(): IKsyosAccountDetails {
    return {
        startDate: DateTime.now().format('YYYY-MM-DD'),
        userName: '',
    };
}
